<template>
  <div>
    <el-alert
      title="This section is optional."
      type="info"
      show-icon
    />

    <sh-spacer by="3"/>
    <h2>Additional Details</h2>

    <el-row>
      <el-col>
        <el-form
          ref="form"
          :model="form"
          :label-position="'top'"
        >

          <el-form-item label="Referral code">
            <el-input
              v-model="form.referral"
              :disabled="!editablePreReview"
              placeholder="Who referred you to submit your film?"
            ></el-input>
          </el-form-item>

          <el-form-item label="Premiere">
            <el-checkbox
              v-model="form.premiere"
              :disabled="!editable"
              @change="updateForm"
            >
              This is an online premiere.
            </el-checkbox>
          </el-form-item>

          <el-form-item label="Film festival">
            <div class="instruction">
              Please enter one festival for each bullet point.
            </div>
            <quill-editor
              v-model="form.festivals"
              :disabled="!editable"
              :options="editorOption"
              ref="myQuillEditor"
              @change="updateFormDebounce()"
              @focus="focusFilmFestival"
              @blur="blurFilmFestival"
            >

            </quill-editor>
          </el-form-item>

          <sh-spacer by="6"/>

          <h3>Additional information for the film</h3>

          <el-form-item label="Director’s Statement">
            <el-input
              v-model="form.director_statement"
              :disabled="!editable"
              type="textarea"
              :autosize="{ minRows: 6 }"
            ></el-input>
            <span>Share your thoughts on what inspired you to make this film, and why this message is important to tell our audience in maximum 150 words.</span>
          </el-form-item>

          <el-form-item label="Logline">
            <el-input
              v-model="form.description_short"
              :disabled="!editable"
            ></el-input>
          </el-form-item>

          <sh-spacer by="6"/>

          <h3>Social</h3>

          <el-form-item label="Facebook Page">
            <el-input
              v-model="form.fb_page_url"
              :disabled="!editable"
              placeholder="https://www.facebook.com/..."
            ></el-input>
          </el-form-item>

          <el-form-item label="YouTube Channel">
            <el-input
              v-model="form.youtube_channel"
              :disabled="!editable"
              placeholder="https://www.youtube.com/channel/..."
            ></el-input>
          </el-form-item>

          <el-form-item label="Twitter">
            <el-input
              v-model="form.tw_handle"
              :disabled="!editable"
              placeholder="Twitter handle"
            >
              <template slot="prepend">@</template>
            </el-input>
          </el-form-item>

          <el-form-item label="TikTok">
            <el-input
              v-model="form.tiktok_handle"
              :disabled="!editable"
              placeholder="TikTok handle"
            >
              <template slot="prepend">@</template>
            </el-input>
            <span>If you're submitting for Juree Awards Asia 2023 - Vertical Category, kindly indicate your Tiktok handle.</span>
          </el-form-item>

          <el-form-item label="Trailer URL">
            <el-input
              v-model="form.trailer_url"
              :disabled="!editable"
              placeholder="http://..."
            ></el-input>
          </el-form-item>

        </el-form>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { quillEditor } from 'vue-quill-editor';
import formsMixin from '@/mixins/formsMixin';
import stepMixin from '../Mixins/stepMixin';

export default {
  name: 'additional',
  mixins: [
    formsMixin,
    stepMixin,
  ],
  components: {
    quillEditor,
  },
  props: [
    'value',
  ],
  data() {
    return {
      form: {
        director_statement: this.value.director_statement,
        referral: this.value.referral,
        description_short: this.value.description_short,
        premiere: this.value.premiere,
        festivals: this.value.festivals,
        // Social
        fb_page_url: this.value.fb_page_url,
        youtube_channel: this.value.youtube_channel,
        tw_handle: this.value.tw_handle,
        tiktok_handle: this.value.tiktok_handle,
        trailer_url: this.value.trailer_url,
      },
      editorOption: {
        modules: {
          toolbar: [
            [{ list: 'bullet' }],
          ],
        },
        placeholder: 'Example: Jury Prize, Busan International Film Festival, 2013',
        height: '200px',
      },
      updateFormDebounce: debounce(this.updateForm, 700),
      filmFestivalFocus: false,
    };
  },
  computed: {
    isFilmFestivalFilled() {
      return !!this.value.festivals;
    },
    shouldRenderFilmFestivalPlaceholder() {
      return !this.isFilmFestivalFilled && !this.filmFestivalFocus;
    },
  },
  methods: {
    updateForm() {
      this.$emit('input', {
        ...this.form,
      });
    },
    focusFilmFestival() {
      this.filmFestivalFocus = true;
      if (!this.isFilmFestivalFilled) {
        this.form.festivals = '<ul><li> </li></ul>';
      }
    },
    blurFilmFestival() {
      this.filmFestivalFocus = false;
    },

    setReferralFromRemote() {
      if (localStorage.getItem('creator')) {
        const creatorJson = localStorage.getItem('creator');
        try {
          const creatorData = JSON.parse(creatorJson);

          // Validate referral code
          const referralCode = creatorData.referralCode;
          const isValid = ((new Date()).getTime() - creatorData.timestamp) < 86400000; // 1 days

          // Prefill referral code
          if (isValid) {
            this.form.referral = referralCode;
            this.$emit('input', {
              referral: referralCode,
            });
          }

          // Clean up referral code
          delete creatorData.referralCode;
          delete creatorData.timestamp;
          window.localStorage.setItem('creator', JSON.stringify(creatorData));
        } catch (e) {
          throw e;
        }
      }
    },
  },
  mounted() {
    if (!this.value.referral) { // prefill referral only when it is empty
      this.setReferralFromRemote();
    }
  },
};
</script>

<style lang="scss" scoped>
.ql-editor {
  ul {
    line-height: 1.8em;
    padding-left: 0;

    li:not(.ql-direction-rtl)::before {
      margin-right: 0.8em;
    }
  }

  &.ql-blank::before {
    font-style: normal;
    opacity: 0.5;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Submission/steps/AdditionalDetail.vue